import React from 'react';
import PropTypes from 'prop-types';

import {Paper, Stack, Typography, Box, Menu, MenuItem} from 'views/components';
import {InfoOutlined} from 'views/icons';
import {Translate, translate} from 'views/module';

import ScheduleEventAdd from './schedule_events/ScheduleEventAdd';
import ScheduleEventDisplayFlexible from './schedule_events/ScheduleEventDisplayFlexible';
import ScheduleEventDisplayOncall from './schedule_events/ScheduleEventDisplayOncall';
import ScheduleEventDisplayRecurring from './schedule_events/ScheduleEventDisplayRecurring';
import ScheduleEventEditFlexible from './schedule_events/ScheduleEventEditFlexible';
import ScheduleEventEditOncall from './schedule_events/ScheduleEventEditOncall';
import ScheduleEventEditRecurring from './schedule_events/ScheduleEventEditRecurring';

const ScheduleCard = (props) => {
  const {events, onEventsChange} = props;

  const [isAdding, setIsAdding] = React.useState(false);
  const [editingEventId, setEditingEventId] = React.useState(null);
  const [selectedScheduleType, setSelectedScheduleType] = React.useState('RECURRING');
  const [menuAnchorEl, setMenuAnchorEl] = React.useState(null);
  const [menuItems, setMenuItems] = React.useState([]);

  const handleMenuClose = React.useCallback(() => {
    setMenuAnchorEl(null);
    setMenuItems([]);
  }, []);

  const resetEditorState = React.useCallback(() => {
    setEditingEventId(null);
    setIsAdding(false);
    setSelectedScheduleType('RECURRING');
  }, []);

  const handleAddStart = React.useCallback((scheduleType) => {
    setEditingEventId(null);
    setSelectedScheduleType(scheduleType);
    setIsAdding(true);
  }, []);

  const handleSave = React.useCallback((eventPayload) => {
    const nextEvent = {
      id: editingEventId || `event-${Date.now()}`,
      ...eventPayload,
    };

    let nextEvents = [...events, nextEvent];
    if (editingEventId) {
      nextEvents = events.map((eventItem) => (eventItem.id === editingEventId ? nextEvent : eventItem));
    }

    onEventsChange(nextEvents);
    resetEditorState();
  }, [editingEventId, events, onEventsChange, resetEditorState]);

  const handleRemoveDraft = React.useCallback(() => {
    if (editingEventId) {
      const nextEvents = events.filter((eventItem) => eventItem.id !== editingEventId);
      onEventsChange(nextEvents);
    }

    resetEditorState();
    handleMenuClose();
  }, [editingEventId, events, handleMenuClose, onEventsChange, resetEditorState]);

  const handleMenuOpen = React.useCallback((event, nextMenuItems) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuItems(nextMenuItems);
  }, []);

  const handleEditCareTime = React.useCallback((eventId) => {
    const eventToEdit = events.find((eventItem) => eventItem.id === eventId);
    if (!eventToEdit) {
      handleMenuClose();
      return;
    }

    const eventScheduleType = eventToEdit.scheduleType;
    setEditingEventId(eventToEdit.id);
    setSelectedScheduleType(eventScheduleType);
    setIsAdding(true);
    handleMenuClose();
  }, [events, handleMenuClose]);

  const activeDraftKey = React.useMemo(() => `${selectedScheduleType}-${editingEventId || 'new'}`, [editingEventId, selectedScheduleType]);
  const editingEvent = React.useMemo(() => {
    if (!editingEventId) {
      return null;
    }

    return events.find((eventItem) => eventItem.id === editingEventId) || null;
  }, [editingEventId, events]);

  const visibleEvents = React.useMemo(() => {
    if (!editingEventId) {
      return events;
    }

    return events.filter((eventItem) => eventItem.id !== editingEventId);
  }, [events, editingEventId]);

  const recurringVisibleEvents = React.useMemo(() => {
    return visibleEvents.filter((eventItem) => eventItem.scheduleType === 'RECURRING');
  }, [visibleEvents]);

  const flexibleVisibleEvents = React.useMemo(() => {
    return visibleEvents.filter((eventItem) => eventItem.scheduleType === 'FLEXIBLE');
  }, [visibleEvents]);

  const onCallVisibleEvents = React.useMemo(() => {
    return visibleEvents.filter((eventItem) => eventItem.scheduleType === 'ON_CALL');
  }, [visibleEvents]);

  return (
    <Paper
      sx={{
        p: '24px',
        borderRadius: '20px',
        boxShadow: 'none',
      }}
    >
      <Stack sx={{gap: '32px'}}>
        <Typography sx={{fontSize: '24px', fontWeight: 400, color: '#2D2925', lineHeight: '128%', fontFamily: '"Spectral", serif'}}>
          <Translate expression={'schedulestep.ScheduleCard.Title'} />
        </Typography>

        <Box
          sx={{
            p: '24px',
            borderRadius: '16px',
            backgroundColor: '#DA795514',
          }}
        >
          <Box sx={{display: 'flex', alignItems: 'center', gap: '8px', mb: '12px'}}>
            <InfoOutlined sx={{fontSize: 24, color: '#D97A52'}} />
            <Typography sx={{fontSize: '16px', fontWeight: 500, color: '#2D2925', lineHeight: '150%'}}>
              <Translate expression={'schedulestep.Common.PleaseNote'} />
            </Typography>
          </Box>
          <Typography sx={{fontSize: '16px', fontWeight: 400, color: '#2D2925', lineHeight: '150%'}}>
            <Translate expression={'schedulestep.ScheduleCard.Description'} />
          </Typography>
        </Box>

        <Stack sx={{gap: '16px'}}>
          <ScheduleEventDisplayRecurring
            events={recurringVisibleEvents}
            onMenuClick={handleMenuOpen}
          />

          {
            flexibleVisibleEvents.map((eventItem) => (
              <ScheduleEventDisplayFlexible
                key={eventItem.id}
                preferredDays={eventItem.preferredDays}
                preferredTimes={eventItem.preferredTimes}
                visitDuration={eventItem.visitDuration}
                onMenuClick={(event) => handleMenuOpen(event, [{
                  id: eventItem.id,
                  label: translate('schedulestep.ScheduleEventAdd.FlexibleTitle'),
                }])}
              />
            ))
          }

          {
            onCallVisibleEvents.map((eventItem) => (
              <ScheduleEventDisplayOncall
                key={eventItem.id}
                visitLength={eventItem.visitLength}
                onMenuClick={(event) => handleMenuOpen(event, [{
                  id: eventItem.id,
                  label: translate('schedulestep.ScheduleEventAdd.OnCallTitle'),
                }])}
              />
            ))
          }

          {
            isAdding &&
            (
              selectedScheduleType === 'FLEXIBLE' &&
                <ScheduleEventEditFlexible
                  key={activeDraftKey}
                  initialEvent={editingEvent}
                  onSave={handleSave}
                  onRemove={handleRemoveDraft}
                />
            )
          }

          {
            isAdding &&
            selectedScheduleType === 'ON_CALL' &&
            (
              <ScheduleEventEditOncall
                key={activeDraftKey}
                initialEvent={editingEvent}
                onSave={handleSave}
                onRemove={handleRemoveDraft}
              />
            )
          }

          {
            isAdding &&
            selectedScheduleType !== 'FLEXIBLE' &&
            selectedScheduleType !== 'ON_CALL' &&
            (
              <ScheduleEventEditRecurring
                key={activeDraftKey}
                initialEvent={editingEvent}
                onSave={handleSave}
                onRemove={handleRemoveDraft}
              />
            )
          }

          {
            !isAdding &&
            <ScheduleEventAdd
              onAddClick={handleAddStart}
              selectedOptionId={selectedScheduleType}
              onSelectedOptionIdChange={setSelectedScheduleType}
            />
          }
        </Stack>

        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
          transformOrigin={{vertical: 'top', horizontal: 'right'}}
          slotProps={{
            paper: {
              sx: {
                mt: '8px',
                borderRadius: '12px',
                border: '1px dashed #C5BBB9',
                boxShadow: 'none',
                backgroundColor: '#FFFFFF',
                minWidth: 220,
              },
            },
            list: {
              sx: {
                p: '4px',
              },
            },
          }}
        >
          {
            menuItems.map((menuItem) => (
              <MenuItem
                key={menuItem.id}
                onClick={() => handleEditCareTime(menuItem.id)}
                sx={{
                  'borderRadius': '8px',
                  'px': '12px',
                  'py': '10px',
                  'fontSize': '16px',
                  'fontWeight': 400,
                  'color': '#2D2925',
                  'textTransform': 'capitalize',
                  '&:hover': {
                    backgroundColor: '#DA795514',
                  },
                  '&.Mui-focusVisible': {
                    backgroundColor: '#DA795514',
                  },
                }}
              >
                <Translate
                  expression={'schedulestep.ScheduleCard.EditCare'}
                  substitute={{careLabel: menuItem.label}}
                />
              </MenuItem>
            ))
          }
        </Menu>
      </Stack>
    </Paper>
  );
};

ScheduleCard.propTypes = {
  events: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    label: PropTypes.string,
    startTime: PropTypes.string,
    endTime: PropTypes.string,
    repeats: PropTypes.arrayOf(PropTypes.string),
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    scheduleType: PropTypes.string,
    preferredDays: PropTypes.arrayOf(PropTypes.string),
    preferredTimes: PropTypes.arrayOf(PropTypes.string),
    visitDuration: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    visitLength: PropTypes.string,
  })),
  onEventsChange: PropTypes.func,
};

export default React.memo(ScheduleCard);
